// Helpers for the self-service area (พื้นที่ของฉัน, /me).
// Backend: /api/me/* — every call is scoped to the session's own employee_id.

export { fmtDateTH, relabelBranch } from './asset.js'
export { BANKS } from './employee.js'
export { fmtBytes, isImageFile, fileTagText } from './dentist.js'

// Document categories a staff member files about themselves. Free text is not
// offered — a fixed set is what makes the list scannable for HR later.
export const MY_FILE_TAGS = [
  'บัตรประชาชน', 'ทะเบียนบ้าน', 'วุฒิการศึกษา', 'ใบประกอบวิชาชีพ',
  'สัญญาจ้าง', 'บัญชีธนาคาร', 'ใบรับรองแพทย์', 'อื่นๆ',
]

/**
 * The delete window, mirrored from functions/api/me/_me.js.
 *
 * This copy shapes the UI (whether the button is even drawn); the server copy is
 * the rule. Keep the two in step — a UI that offers a delete the server refuses
 * is worse than no button at all.
 */
export const DELETE_WINDOW_MS = 24 * 60 * 60 * 1000

export const fileHref = (f) => (
  f?.location
    ? '/api/me/file/' + String(f.location).split('/').map(encodeURIComponent).join('/')
    : (f?.url || '')
)

// Milliseconds left in the 24h window, or 0 once it has closed. An entry with no
// usable created_at reads as closed — same fail-closed stance as the server.
export function deleteWindowLeft(entry, now = Date.now()) {
  const created = Number(entry?.created_at || 0)
  if (!Number.isFinite(created) || created <= 0) return 0
  return Math.max(0, created + DELETE_WINDOW_MS - now)
}

// Does the UI draw a delete button for this entry? Admins always; the uploader
// only inside the window.
export function canDeleteFile(entry, me, now = Date.now()) {
  if (isAdmin(me)) return true
  const uploader = Number(entry?.user_id || 0)
  if (!uploader || uploader !== Number(me?.employee_id || 0)) return false
  return deleteWindowLeft(entry, now) > 0
}

function isAdmin(me) {
  return Array.isArray(me?.roles) && me.roles.includes('admin')
}

// "เหลืออีก 3 ชม. 12 นาที" — how long the uploader can still take it back.
export function fmtWindowLeft(ms) {
  if (ms <= 0) return ''
  const mins = Math.floor(ms / 60000)
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (h > 0) return `เหลืออีก ${h} ชม.${m ? ` ${m} นาที` : ''}`
  return `เหลืออีก ${Math.max(1, m)} นาที`
}

// Absolute Bangkok date+time for an epoch-ms stamp. `fmtDateTH` renders the date
// alone; an upload needs the clock too, since the window is measured in hours.
export function fmtDateTimeTH(v) {
  const n = Number(v)
  if (!Number.isFinite(n) || n <= 0) return '—'
  return new Date(n).toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// Show the tail only — enough for the owner to confirm the number is theirs,
// without putting the whole thing on screen in an open-plan office.
export function maskTail(value, keep = 4) {
  const s = String(value || '').trim()
  if (!s) return '—'
  if (s.length <= keep) return s
  return '•'.repeat(Math.min(8, s.length - keep)) + s.slice(-keep)
}

// Employee bank numbers are stored as bare digits; group them so they read like
// the passbook they were copied from.
export function fmtBankNum(v) {
  const d = String(v || '').replace(/\D+/g, '')
  if (d.length !== 10) return d || ''
  return `${d.slice(0, 3)}-${d.slice(3, 4)}-${d.slice(4, 9)}-${d.slice(9)}`
}
